import { supabase, publicUrl } from "./supabase";

/* ============================================================
 * Property + Unit-Type queries and mutations.
 * The public site uses the anonymous read policies; admin ops
 * require an authenticated session (see /admin).
 * ============================================================ */

const STATUS_LABEL = {
  now_leasing: "Now leasing",
  fully_leased: "Fully leased · Waitlist",
  coming_soon: "Coming soon",
  draft: "Draft",
};

export const statusLabel = (s) => STATUS_LABEL[s] || s;

export const money = (n) => (n == null ? "" : `$${Number(n).toLocaleString()}`);

/** From-price line: uses rent_from across all unit types. */
export const fromPrice = (unitTypes = []) => {
  const rents = unitTypes.map((u) => u.rent_from).filter(Boolean);
  if (!rents.length) return null;
  return Math.min(...rents);
};

/** ---- Public reads ---- */

export const listProperties = async () => {
  const { data, error } = await supabase
    .from("properties")
    .select("*, unit_types(*)")
    .neq("status", "draft")
    .order("featured", { ascending: false })
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []).map(hydrate);
};

export const getPropertyBySlug = async (slug) => {
  const { data, error } = await supabase
    .from("properties")
    .select("*, unit_types(*)")
    .eq("slug", slug)
    .neq("status", "draft")
    .maybeSingle();
  if (error) throw error;
  return data ? hydrate(data) : null;
};

/** ---- Admin reads ---- */
export const listAllPropertiesAdmin = async () => {
  const { data, error } = await supabase
    .from("properties")
    .select("*, unit_types(*)")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []).map(hydrate);
};

export const getPropertyByIdAdmin = async (id) => {
  const { data, error } = await supabase
    .from("properties")
    .select("*, unit_types(*)")
    .eq("id", id)
    .single();
  if (error) throw error;
  return hydrate(data);
};

/** Parse a unit_type's floorplan_image field. Supports two shapes:
 *   - A single storage path (legacy) → returns [{image, label:"Plan", caption:""}]
 *   - A JSON-encoded array of {image, label, caption} → parsed & URL-resolved
 */
const parseFloorplans = (raw) => {
  if (!raw) return [];
  const s = String(raw).trim();
  if (s.startsWith("[") || s.startsWith("{")) {
    try {
      const arr = JSON.parse(s);
      const list = Array.isArray(arr) ? arr : [arr];
      return list
        .filter((p) => p && p.image)
        .map((p) => ({
          image: p.image,
          label: p.label || "Plan",
          caption: p.caption || "",
        }));
    } catch {
      /* fall through */
    }
  }
  return [{ image: s, label: "Plan", caption: "" }];
};

/** Sort unit types by sort_order and resolve image URLs. */
const hydrate = (p) => {
  const unit_types = (p.unit_types || [])
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((u) => {
      const plans = parseFloorplans(u.floorplan_image);
      return {
        ...u,
        photos_resolved: (u.photos || []).map(publicUrl),
        floorplan_resolved: publicUrl(plans[0]?.image),
        floorplan_plans: plans.map((p) => ({
          ...p,
          image_resolved: publicUrl(p.image),
        })),
      };
    });
  return {
    ...p,
    hero_images_resolved: (p.hero_images || []).map(publicUrl),
    unit_types,
  };
};

/** ---- Mutations (admin) ---- */

export const upsertProperty = async (payload) => {
  const { unit_types, unit_types_resolved, ...rest } = payload;
  const isNew = !rest.id;
  const q = isNew
    ? supabase.from("properties").insert(rest).select().single()
    : supabase.from("properties").update(rest).eq("id", rest.id).select().single();
  const { data, error } = await q;
  if (error) throw error;
  return data;
};

export const deletePropertyById = async (id) => {
  const { error } = await supabase.from("properties").delete().eq("id", id);
  if (error) throw error;
};

export const duplicateProperty = async (p) => {
  const base = { ...p };
  delete base.id;
  delete base.created_at;
  delete base.updated_at;
  delete base.unit_types;
  delete base.hero_images_resolved;
  base.slug = `${p.slug}-copy-${Date.now().toString(36).slice(-4)}`;
  base.name = `${p.name} (copy)`;
  base.status = "draft";
  base.featured = false;
  const { data, error } = await supabase
    .from("properties")
    .insert(base)
    .select()
    .single();
  if (error) throw error;

  // clone unit types
  const cloneRows = (p.unit_types || []).map((u) => {
    const c = { ...u };
    delete c.id;
    delete c.created_at;
    delete c.updated_at;
    delete c.photos_resolved;
    delete c.floorplan_resolved;
    c.property_id = data.id;
    return c;
  });
  if (cloneRows.length) {
    const { error: e2 } = await supabase.from("unit_types").insert(cloneRows);
    if (e2) throw e2;
  }
  return data;
};

export const upsertUnitType = async (payload) => {
  const { photos_resolved, floorplan_resolved, ...rest } = payload;
  const isNew = !rest.id;
  const doUpsert = (p) =>
    isNew
      ? supabase.from("unit_types").insert(p).select().single()
      : supabase.from("unit_types").update(p).eq("id", p.id).select().single();
  let { data, error } = await doUpsert(rest);
  // Graceful fallback: if the `subtitle` column doesn't exist yet, retry without it.
  if (error && /subtitle/i.test(error.message || "")) {
    const { subtitle, ...trimmed } = rest;
    ({ data, error } = await doUpsert(trimmed));
  }
  if (error) throw error;
  return data;
};

export const deleteUnitType = async (id) => {
  const { error } = await supabase.from("unit_types").delete().eq("id", id);
  if (error) throw error;
};

/** Client-side image compression + Storage upload. */
export const uploadImage = async (file, folder = "misc") => {
  const imageCompression = (await import("browser-image-compression")).default;
  const compressed = await imageCompression(file, {
    maxSizeMB: 0.5,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
    fileType: "image/jpeg",
    initialQuality: 0.85,
  });
  const ext = "jpg";
  const path = `${folder}/${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage
    .from("properties")
    .upload(path, compressed, {
      cacheControl: "31536000",
      upsert: false,
      contentType: "image/jpeg",
    });
  if (error) throw error;
  return path;
};

/** Booking-form URL builder (deep links per unit-type + source). */
export const bookingLink = ({ propertySlug, unitTypeName, source = "website" }) => {
  const key = unitTypeName
    ? `${propertySlug}-${slugify(unitTypeName)}`
    : propertySlug;
  const params = new URLSearchParams({ property: key, source });
  return `/contact?${params.toString()}`;
};

export const slugify = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
